import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import type { EcoStats } from "../hooks/use-eco";

interface EcoStore {
  // State
  stats: EcoStats | undefined;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;

  // Actions
  setStats: (stats: EcoStats) => void;
  updateStats: (updates: Partial<EcoStats>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearErrors: () => void;

  // Selectors
  getStats: () => EcoStats | undefined;
  getIndex: () => number;
  getUsage: () => EcoStats["usage"] | undefined;
  getMessages: () => EcoStats["messages"] | undefined;
}

export const useEcoStore = create<EcoStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      stats: undefined,
      isLoading: false,
      error: null,
      lastUpdated: 0,

      // Actions
      setStats: (stats: EcoStats) => {
        set({
          stats,
          lastUpdated: Date.now(),
          isLoading: false,
          error: null,
        });
      },

      updateStats: (updates: Partial<EcoStats>) => {
        set((state) => ({
          stats: state.stats ? { ...state.stats, ...updates } : undefined,
          lastUpdated: Date.now(),
        }));
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error, isLoading: false });
      },

      clearErrors: () => {
        set({ error: null });
      },

      // Selectors
      getStats: () => get().stats,

      getIndex: () => get().stats?.index || 0,

      getUsage: () => get().stats?.usage,

      getMessages: () => get().stats?.messages,
    })),
    {
      name: "eco-store", // Name that will appear in Redux DevTools
    }
  )
);
